/**
 * API Client for communicating with the backend server
 */

import type {
  Organization,
  OrganizationPublic,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  OrganizationUser,
  AddMemberDto,
  MemberDetail,
  Case,
  CreateCaseDto,
  UpdateCaseDto,
  CaseAssignment,
  AssignUserToCaseDto,
  CaseStatus,
  User,
  UpdateUserDto,
} from "../../types";
import type {
  AuditLogsResponse,
  AuditLogFilters,
  ExportAuditLogsParams,
} from "../../types/audit";
import type {
  FolderResponseDto,
  FolderItemResponseDto,
  CreateFolderDto,
  UpdateFolderDto,
  AddItemToFolderDto,
  FileResponseDto,
  FormSubmissionListItemDto,
  FormSubmissionResponseDto,
  ParticipantByTokenDto,
  MagicLinkConfirmDto,
  ShareLinkInfoDto,
  CreateRequestUiDto,
} from "@secure-caseworker/shared";
import { API_BASE_URL } from "../../config/constants";
import {
  FileShareResponse,
  FormShareResponse,
  ListSharesResponse,
  ReshareRequest,
  ReshareResponse,
  SubmitFormRequest,
  SubmitFormResponse,
  UploadFileRequest,
  UploadFileResponse,
} from "./types";
import {
  CaseFormResponseDto,
  CreateCaseFormDto,
  FormResponseDto,
  UpdateCaseFormDto,
  UpdateFormDto,
  CreateRequestTemplateDto,
  UpdateRequestTemplateDto,
  RequestTemplate,
  RequestTemplateResponse,
  Request,
  UpdateRequestDto,
} from "@secure-caseworker/shared";
import { BrowserPdfDocument } from "../../contexts/PdfManagementContext";

interface ApiError {
  error: string;
  details?: any;
}

export type UsageSummary = {
  plan: {
    planId: string;
    planName: string;
    envelopeQuotaYear: number;
    userHardLimit: number | null;
    storageLimitBytes: string | null; // bigint serialized as string
    overageEnvelopeCents: number | null;
    overageStorageCentsPerMb: number | null;
    overageAllowed: boolean;
  };
  envelopes: {
    planId: string;
    quota: number;
    used: number;
    remaining: number;
    overageCount: number;
    percentUsed: number;
    isLowQuota: boolean;
    overageAllowed: boolean;
    overageRateCents: number | null;
  };
  storage: {
    limitBytes: string | null; // bigint serialized as string
    limitMb: number | null;
    usedBytes: string; // bigint serialized as string
    usedMb: number;
    remainingMb: number | null;
    overageMb: number;
    percentUsed: number | null;
  };
  users: {
    limit: number | null;
    count: number;
    remaining: number | null;
  };
};

export type SearchResult = {
  cases: Array<{
    id: string;
    title: string;
    status: string;
    updatedAt: string;
  }>;
  files: Array<{
    id: string;
    filename: string;
    caseId: string | null;
    caseTitle: string | null;
    updatedAt: string;
  }>;
};

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (this.authToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${this.authToken}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: "include", // Include cookies for HttpOnly session tokens
    });

    if (response.status === 204) {
      return undefined as T;
    }

    // Check if the response is JSON before parsing
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      const responseContent = await response.text();
      console.log(responseContent, "responseContent");
      throw new Error(
        `Server returned ${response.status}: Expected JSON but received ${
          contentType || "unknown content type"
        }`,
      );
    }

    const data = await response.json();

    if (!response.ok) {
      console.error(`API Error [${response.status}]:`, {
        endpoint,
        method: options.method || "GET",
        error: data,
      });
      // NestJS returns errors as { statusCode, message, error }
      // Prioritize 'message' over 'error' for more specific error details
      let errorMessage =
        (data as any).message || (data as ApiError).error || "Request failed";

      // Handle validation errors that come as array of strings or ValidationError objects
      if (Array.isArray(errorMessage)) {
        if (typeof errorMessage[0] === "string") {
          // Array of validation message strings
          errorMessage = errorMessage[0];
        } else if (errorMessage[0]?.constraints) {
          // Array of ValidationError objects from class-validator
          const constraints = errorMessage[0].constraints;
          errorMessage = Object.values(constraints)[0] as string;
        } else {
          errorMessage = "Validation failed";
        }
      }

      throw new Error(errorMessage);
    }

    return data as T;
  }

  /**
   * Make a request that returns a blob (for file downloads)
   * Returns the Response object so caller can extract blob and headers
   */
  private async requestBlob(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const headers: HeadersInit = {
      ...options.headers,
    };

    if (this.authToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${this.authToken}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: "include", // Include cookies for HttpOnly session tokens
    });

    if (!response.ok) {
      // Try to parse error as JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const error = await response.json();
        // NestJS returns errors as { statusCode, message, error }
        const errorMessage =
          (error as any).message ||
          (error as ApiError).error ||
          "Request failed";
        throw new Error(errorMessage);
      }
      throw new Error(`Request failed: ${response.statusText}`);
    }

    return response;
  }

  // File operations
  async uploadFile(data: UploadFileRequest): Promise<UploadFileResponse> {
    return this.request<UploadFileResponse>("/files/upload", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getFileByShareToken(
    shareToken: string,
    encryptionKey: string,
  ): Promise<FileShareResponse> {
    return this.request<FileShareResponse>(
      `/files/share/${shareToken}?key=${encryptionKey}`,
    );
  }

  async listShares(): Promise<ListSharesResponse> {
    return this.request<ListSharesResponse>("/files/shares");
  }

  async listFiles(_userId?: string): Promise<FileResponseDto[]> {
    return this.request<FileResponseDto[]>("/files");
  }

  async listFilesByCase(caseId: string): Promise<FileResponseDto[]> {
    return this.request<FileResponseDto[]>(`/files?caseId=${caseId}`);
  }

  async getFile(fileId: string): Promise<FileResponseDto> {
    return this.request<FileResponseDto>(`/files/${fileId}`);
  }

  async deleteFile(fileId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/files/${fileId}`, {
      method: "DELETE",
    });
  }

  // Folder operations
  async createFolder(data: CreateFolderDto): Promise<FolderResponseDto> {
    console.log("data", data);
    return this.request<FolderResponseDto>("/folders", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateFolder(
    folderId: string,
    data: UpdateFolderDto,
  ): Promise<FolderResponseDto> {
    return this.request<FolderResponseDto>(`/folders/${folderId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getFolderContents(folderId: string): Promise<FolderItemResponseDto[]> {
    return this.request<FolderItemResponseDto[]>(
      `/folders/${folderId}/contents`,
    );
  }

  async getRootFolders(caseId: string): Promise<FolderResponseDto[]> {
    return this.request<FolderResponseDto[]>(`/folders/case/${caseId}/root`);
  }

  async getAllFolders(caseId: string): Promise<FolderResponseDto[]> {
    return this.request<FolderResponseDto[]>(`/folders/case/${caseId}`);
  }

  async addItemToFolder(
    folderId: string,
    data: AddItemToFolderDto,
  ): Promise<FolderItemResponseDto> {
    return this.request<FolderItemResponseDto>(`/folders/${folderId}/items`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async removeItemFromAllFolders(
    itemId: string,
    itemType: "file" | "folder",
  ): Promise<any> {
    return this.request<any>(`/folders/items/remove`, {
      method: "POST",
      body: JSON.stringify({ itemId, itemType }),
    });
  }

  async deleteFolder(folderId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/folders/${folderId}`, {
      method: "DELETE",
    });
  }

  async revokeShare(shareId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/files/share/${shareId}`, {
      method: "DELETE",
    });
  }

  async reshare(
    shareId: string,
    data: ReshareRequest,
  ): Promise<ReshareResponse> {
    return this.request<ReshareResponse>(`/files/reshare/${shareId}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async createCaseForm(data: CreateCaseFormDto): Promise<CaseFormResponseDto> {
    return this.request<CaseFormResponseDto>(`/case-forms`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateCaseForm(
    caseFormId: string,
    data: UpdateCaseFormDto,
  ): Promise<CaseFormResponseDto> {
    return this.request<CaseFormResponseDto>(`/case-forms/${caseFormId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteCaseForm(caseFormId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/case-forms/${caseFormId}`, {
      method: "DELETE",
    });
  }

  async getFolders(caseId: string): Promise<FolderResponseDto[]> {
    return this.request<FolderResponseDto[]>(`/folders/case/${caseId}`);
  }

  // Participant-based public form operations (no auth required)
  async getParticipantByToken(token: string): Promise<ParticipantByTokenDto> {
    return this.request(`/participants/public/${token}`);
  }

  async submitParticipantForm(
    participantId: string,
    requestId: string,
    fieldData: any,
    token: string,
    folderId: string,
  ): Promise<{ success: boolean; submissionId: string }> {
    return this.request(`/participants/${participantId}/submit-form`, {
      method: "POST",
      body: JSON.stringify({ requestId, fieldData, token, folderId }),
    });
  }

  /**
   * Upload a file for a participant form field (public, no auth required)
   */
  async uploadParticipantFile(
    participantId: string,
    file: File,
    token: string,
    folderId: string,
    onProgress: (percentComplete: number) => void,
  ): Promise<{
    success: boolean;
    file: { id: string; name: string; size: number };
  }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("token", token);
    formData.append("folderId", folderId);

    return this.uploadFileWithProgress(
      `/participants/${participantId}/upload-file`,
      formData,
      onProgress,
    );
  }

  async verifyParticipantEmail(verificationToken: string): Promise<{
    success: boolean;
    participantToken?: string;
    email?: string;
    error?: string;
  }> {
    return this.request(`/participants/verify-email`, {
      method: "POST",
      body: JSON.stringify({ verificationToken }),
    });
  }

  async requestCeremony(
    participantToken: string,
    verificationToken: string,
  ): Promise<{ expiresAt: string; ceremonyUrl: string }> {
    return this.request(`/participants/request-ceremony`, {
      method: "POST",
      body: JSON.stringify({ participantToken, verificationToken }),
    });
  }

  // Public form submission - no verified_email param, backend checks verification state
  async getFormSubmissionByToken(
    token: string,
  ): Promise<FormSubmissionResponseDto> {
    return this.request<FormSubmissionResponseDto>(
      `/form-submissions/public/${token}`,
    );
  }

  /**
   * Download a file attached to a shared form submission (public)
   * Uses the share token for authorization
   */
  async downloadFormSubmissionFile(
    shareToken: string,
    fileId: string,
  ): Promise<Response> {
    return this.requestBlob(
      `/form-submissions/public/${shareToken}/files/${fileId}/download`,
    );
  }

  async getFormByShareToken(shareToken: string): Promise<FormShareResponse> {
    return this.request<FormShareResponse>(`/forms/share/${shareToken}`);
  }

  async submitForm(data: SubmitFormRequest): Promise<SubmitFormResponse> {
    return this.request<SubmitFormResponse>("/forms/submit", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getForm(formId: string): Promise<FormResponseDto> {
    return this.request<FormResponseDto>(`/forms/${formId}`);
  }

  async getForms(organizationId: string): Promise<FormResponseDto[]> {
    return this.request<FormResponseDto[]>(`/forms?orgId=${organizationId}`);
  }

  async updateForm(
    formId: string,
    data: UpdateFormDto,
  ): Promise<FormResponseDto> {
    return this.request<FormResponseDto>(`/forms/${formId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteForm(formId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/forms/${formId}`, {
      method: "DELETE",
    });
  }

  async getFormSubmissions(
    formId: string,
  ): Promise<{ success: boolean; submissions: any[] }> {
    return this.request<{ success: boolean; submissions: any[] }>(
      `/forms/${formId}/submissions`,
    );
  }

  async getFormSubmission(
    submissionId: string,
  ): Promise<FormSubmissionResponseDto> {
    return this.request<FormSubmissionResponseDto>(
      `/form-submissions/${submissionId}`,
    );
  }

  async listSubmissions(
    orgId: string,
    caseId?: string,
  ): Promise<FormSubmissionListItemDto[]> {
    const params = new URLSearchParams({ organizationId: orgId });
    if (caseId) {
      params.append("caseId", caseId);
    }
    return this.request<FormSubmissionListItemDto[]>(
      `/form-submissions?${params.toString()}`,
    );
  }

  async deleteFormSubmission(
    submissionId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/form-submissions/${submissionId}`,
      {
        method: "DELETE",
      },
    );
  }

  async listForms(): Promise<{ success: boolean; forms: any[] }> {
    return this.request<{ success: boolean; forms: any[] }>("/forms");
  }

  // Organization operations
  async createOrganization(data: CreateOrganizationDto): Promise<Organization> {
    return this.request<Organization>("/organizations", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async listOrganizations(): Promise<Organization[]> {
    return this.request<Organization[]>("/organizations");
  }

  async listAllOrganizations(): Promise<Organization[]> {
    return this.request<Organization[]>("/organizations/all");
  }

  async getOrganization(orgId: string): Promise<Organization> {
    return this.request<Organization>(`/organizations/${orgId}`);
  }

  async getOrganizationUsage(orgId: string): Promise<UsageSummary> {
    return this.request<UsageSummary>(`/organizations/${orgId}/usage`);
  }

  async updateOrganization(
    orgId: string,
    data: UpdateOrganizationDto,
  ): Promise<Organization> {
    return this.request<Organization>(`/organizations/${orgId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(orgId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/organizations/${orgId}`, {
      method: "DELETE",
    });
  }

  async getOrganizationPublic(orgId: string): Promise<OrganizationPublic> {
    return this.request<OrganizationPublic>(`/organizations/${orgId}/public`);
  }

  async uploadOrganizationLogo(orgId: string, file: File): Promise<Organization> {
    const formData = new FormData();
    formData.append("file", file);

    const headers: HeadersInit = {};
    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(`${this.baseUrl}/organizations/${orgId}/logo`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to upload logo");
    }

    return response.json();
  }

  async deleteOrganizationLogo(orgId: string): Promise<Organization> {
    return this.request<Organization>(`/organizations/${orgId}/logo`, {
      method: "DELETE",
    });
  }

  async listOrganizationMembers(orgId: string): Promise<OrganizationUser[]> {
    return this.request<OrganizationUser[]>(`/organizations/${orgId}/members`);
  }

  async addOrganizationMember(
    orgId: string,
    data: AddMemberDto,
  ): Promise<OrganizationUser> {
    return this.request<OrganizationUser>(`/organizations/${orgId}/members`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateMemberRole(
    orgId: string,
    userId: string,
    role: string,
  ): Promise<OrganizationUser> {
    return this.request<OrganizationUser>(
      `/organizations/${orgId}/members/${userId}`,
      {
        method: "PUT",
        body: JSON.stringify({ role }),
      },
    );
  }

  async removeOrganizationMember(
    orgId: string,
    userId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/organizations/${orgId}/members/${userId}`,
      {
        method: "DELETE",
      },
    );
  }

  async getMemberDetail(orgId: string, userId: string): Promise<MemberDetail> {
    return this.request<MemberDetail>(
      `/organizations/${orgId}/members/${userId}/detail`,
    );
  }

  // Case operations
  async createCase(data: CreateCaseDto): Promise<Case> {
    return this.request<Case>("/cases", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async listCases(
    organizationId: string,
    status?: CaseStatus,
  ): Promise<Case[]> {
    const params = new URLSearchParams({ organizationId });
    if (status) params.append("status", status);
    return this.request<Case[]>(`/cases?${params.toString()}`);
  }

  async getCase(caseId: string): Promise<Case> {
    return this.request<Case>(`/cases/${caseId}`);
  }

  async getAssignedCases(): Promise<Case[]> {
    return this.request<Case[]>(`/cases/assigned`);
  }

  async updateCase(caseId: string, data: UpdateCaseDto): Promise<Case> {
    return this.request<Case>(`/cases/${caseId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteCase(caseId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/cases/${caseId}`, {
      method: "DELETE",
    });
  }

  // Case assignment operations
  async assignUserToCase(
    caseId: string,
    data: AssignUserToCaseDto,
  ): Promise<CaseAssignment> {
    return this.request<CaseAssignment>(`/cases/${caseId}/assignments`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async assignUsersToCase(
    caseId: string,
    data: { assignments: AssignUserToCaseDto[] },
  ): Promise<CaseAssignment[]> {
    return this.request<CaseAssignment[]>(`/cases/${caseId}/assignments/bulk`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getCaseAssignments(caseId: string): Promise<CaseAssignment[]> {
    return this.request<CaseAssignment[]>(`/cases/${caseId}/assignments`);
  }

  async removeUserFromCase(
    caseId: string,
    userId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/cases/${caseId}/assignments/${userId}`,
      {
        method: "DELETE",
      },
    );
  }

  // Invitation operations
  async createInvitation(
    orgId: string,
    email: string,
    role?: string,
  ): Promise<any> {
    return this.request(`/organizations/${orgId}/invitations`, {
      method: "POST",
      body: JSON.stringify({ email, role }),
    });
  }

  async listInvitations(orgId: string): Promise<any[]> {
    return this.request<any[]>(`/organizations/${orgId}/invitations`);
  }

  async getInvitation(token: string): Promise<any> {
    return this.request(`/invitations/${token}`);
  }

  async acceptInvitation(token: string): Promise<any> {
    return this.request(`/organizations/invitations/${token}/accept`, {
      method: "POST",
    });
  }

  async revokeInvitation(
    orgId: string,
    invitationId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/organizations/${orgId}/invitations/${invitationId}`,
      {
        method: "DELETE",
      },
    );
  }

  // User operations
  async getCurrentUser(): Promise<User> {
    return this.request<User>("/users/me");
  }

  async getUserById(id: string): Promise<User> {
    return this.request<User>(`/users/${id}`);
  }

  async updateCurrentUser(data: UpdateUserDto): Promise<User> {
    return this.request<User>("/users/me", {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getUsersByOrganization(organizationId: string): Promise<User[]> {
    return this.request<User[]>(`/organizations/${organizationId}/users`);
  }

  // Superadmin operations
  async checkSuperadmin(): Promise<{ isSuperadmin: boolean }> {
    return this.request<{ isSuperadmin: boolean }>("/superadmin/check");
  }

  async createOrganizationAsSuperadmin(
    name: string,
    description?: string,
  ): Promise<Organization> {
    return this.request<Organization>("/superadmin/organizations", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    });
  }

  async inviteFirstAdmin(email: string, organizationId: string): Promise<any> {
    return this.request("/superadmin/invitations/first-admin", {
      method: "POST",
      body: JSON.stringify({ email, organizationId }),
    });
  }

  async listSuperadmins(): Promise<any[]> {
    return this.request<any[]>("/superadmin/users");
  }

  async addSuperadmin(userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/superadmin/users/${userId}`, {
      method: "POST",
    });
  }

  async removeSuperadmin(userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/superadmin/users/${userId}`, {
      method: "DELETE",
    });
  }

  // Share Links operations
  async createShareLink(data: any): Promise<any> {
    return this.request("/share-links", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async listShareLinksForResource(
    resourceType: "file" | "form_submission",
    resourceId: string,
  ): Promise<any[]> {
    return this.request<any[]>(
      `/share-links/resource/${resourceType}/${resourceId}`,
    );
  }

  async revokeShareLink(shareLinkId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/share-links/${shareLinkId}`, {
      method: "DELETE",
    });
  }

  async getShareLinkInfo(token: string): Promise<ShareLinkInfoDto> {
    return this.request<ShareLinkInfoDto>(`/share-links/info/${token}`);
  }

  // No email param - backend uses stored recipient
  async requestShareLinkVerification(
    token: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/share-links/verify/request/${token}`,
      {
        method: "POST",
      },
    );
  }

  async confirmShareLink(token: string): Promise<MagicLinkConfirmDto> {
    return this.request(`/share-links/verify/confirm/${token}`);
  }

  /**
   * Download a file via share link token - returns the Response object for processing
   * Uses requestBlob to properly handle binary data and include cookies for session auth
   */
  async downloadShareLinkFile(token: string): Promise<Response> {
    return this.requestBlob(`/share-links/download/${token}`);
  }

  /**
   * Download a file - returns the Response object for processing
   * Automatically includes authentication token
   */
  async downloadFileResponse(
    fileId: string,
    inline?: boolean,
  ): Promise<Response> {
    return this.requestBlob(
      `/files/${fileId}/download${inline ? "?disposition=inline" : ""}`,
    );
  }

  /**
   * Upload a file with FormData and progress tracking
   * Uses XHR to support upload progress events
   */
  async uploadFileWithProgress(
    endpoint: string,
    formData: FormData,
    onProgress: (percentComplete: number) => void,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });

      // Handle completion
      xhr.addEventListener("load", () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error("Failed to parse upload response"));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            // NestJS returns errors as { statusCode, message, error }
            // Prioritize 'message' over 'error' for more specific error details
            const errorMessage =
              errorResponse.message || errorResponse.error || "Upload failed";
            reject(new Error(errorMessage));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      // Handle errors
      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed: Network error"));
      });

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"));
      });

      // Send request with auth token
      xhr.open("POST", `${this.baseUrl}${endpoint}`);
      if (this.authToken) {
        xhr.setRequestHeader("Authorization", `Bearer ${this.authToken}`);
      }
      xhr.send(formData);
    });
  }

  // Audit log operations
  async getAuditLogs(params: AuditLogFilters): Promise<AuditLogsResponse> {
    const queryParams = new URLSearchParams();

    if (params.organizationId)
      queryParams.append("organizationId", params.organizationId);
    if (params.caseId) queryParams.append("caseId", params.caseId);
    if (params.resourceType)
      queryParams.append("resourceType", params.resourceType);
    if (params.resourceId) queryParams.append("resourceId", params.resourceId);
    if (params.action) queryParams.append("action", params.action);
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.dateFrom) queryParams.append("dateFrom", params.dateFrom);
    if (params.dateTo) queryParams.append("dateTo", params.dateTo);
    if (params.isExternalAccess !== undefined) {
      queryParams.append(
        "isExternalAccess",
        params.isExternalAccess.toString(),
      );
    }
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/audit-logs?${queryString}` : "/audit-logs";

    return this.request<AuditLogsResponse>(endpoint);
  }

  async exportAuditLogs(params: ExportAuditLogsParams): Promise<Blob> {
    const queryParams = new URLSearchParams();

    queryParams.append("exportType", params.exportType);
    if (params.organizationId)
      queryParams.append("organizationId", params.organizationId);
    if (params.caseId) queryParams.append("caseId", params.caseId);
    if (params.resourceType)
      queryParams.append("resourceType", params.resourceType);
    if (params.resourceId) queryParams.append("resourceId", params.resourceId);
    if (params.action) queryParams.append("action", params.action);
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.dateFrom) queryParams.append("dateFrom", params.dateFrom);
    if (params.dateTo) queryParams.append("dateTo", params.dateTo);
    if (params.isExternalAccess !== undefined) {
      queryParams.append(
        "isExternalAccess",
        params.isExternalAccess.toString(),
      );
    }
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());

    const queryString = queryParams.toString();
    const endpoint = `/audit-logs/export?${queryString}`;

    const response = await this.requestBlob(endpoint);
    return await response.blob();
  }

  // ---------------------------
  // Beta Signup
  // ---------------------------
  async betaSignup(data: {
    email: string;
    password: string;
    fullName: string;
    company: string;
    betaCode: string;
  }): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/auth/beta-signup",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }

  // ---------------------------
  // Request Templates
  // ---------------------------
  async getRequestTemplates(
    orgId: string,
    type?: "form" | "signature",
  ): Promise<any[]> {
    const params = new URLSearchParams({ organizationId: orgId });
    if (type) params.append("type", type);
    return this.request<any[]>(`/request-templates?${params.toString()}`);
  }

  async getRequestTemplate(
    templateId: string,
    orgId: string,
  ): Promise<RequestTemplateResponse> {
    return this.request<RequestTemplateResponse>(
      `/request-templates/${templateId}?organizationId=${orgId}`,
    );
  }

  async createRequestTemplate(
    data: CreateRequestTemplateDto,
  ): Promise<RequestTemplate> {
    return this.request<RequestTemplate>("/request-templates", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateRequestTemplate(
    templateId: string,
    data: UpdateRequestTemplateDto,
    orgId: string,
  ): Promise<RequestTemplate> {
    return this.request<RequestTemplate>(
      `/request-templates/${templateId}?organizationId=${orgId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  }

  async deleteRequestTemplate(
    templateId: string,
    orgId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/request-templates/${templateId}?organizationId=${orgId}`,
      {
        method: "DELETE",
      },
    );
  }

  async validateRequestTemplate(
    templateId: string,
    orgId: string,
  ): Promise<{ valid: boolean }> {
    return this.request<{ valid: boolean }>(
      `/request-templates/${templateId}/validate?organizationId=${orgId}`,
      {
        method: "POST",
      },
    );
  }

  /**
   * Upload PDFs to a signature request template with optional signature places.
   * Signature places are saved atomically with the PDF upload in a single API call.
   */
  async uploadTemplatePdfs(
    templateId: string,
    orgId: string,
    fileData: BrowserPdfDocument[],
  ): Promise<any> {
    const formData = new FormData();

    fileData
      .filter((doc) => doc.file !== undefined)
      .forEach(({ file, ...metadata }) => {
        formData.append("pdfs", file!);
        formData.append(
          "pdf_metadata",
          JSON.stringify({
            ...metadata,
            signature_places: metadata.signaturePlaces,
          }),
        );
      });

    const headers: HeadersInit = {};
    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(
      `${this.baseUrl}/request-templates/${templateId}/pdfs?organizationId=${orgId}`,
      {
        method: "POST",
        headers,
        body: formData,
      },
    );

    if (!response.ok) {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const error = await response.json();
        throw new Error(error.message || "Upload failed");
      }
      throw new Error(`Upload failed with status ${response.status}`);
    }

    return response.json();
  }

  async deleteTemplatePdf(
    templateId: string,
    pdfFileId: string,
    orgId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/request-templates/${templateId}/pdfs/${pdfFileId}?organizationId=${orgId}`,
      {
        method: "DELETE",
      },
    );
  }

  async updateTemplateSignaturePlaces(
    templateId: string,
    pdfFileId: string,
    signaturePlaces: any[],
    orgId: string,
  ): Promise<any> {
    return this.request<any>(
      `/request-templates/${templateId}/pdfs/${pdfFileId}/signature-places?organizationId=${orgId}`,
      {
        method: "PUT",
        body: JSON.stringify({ signature_places: signaturePlaces }),
      },
    );
  }

  // ---------------------------
  // Requests (Case Request Instances)
  // ---------------------------
  async getRequestsByCase(caseId: string): Promise<Request[]> {
    return this.request<Request[]>(`/requests?caseId=${caseId}`);
  }

  async getRequest(requestId: string): Promise<Request> {
    return this.request<Request>(`/requests/${requestId}`);
  }

  async createRequest(data: CreateRequestUiDto): Promise<Request> {
    return this.request<Request>("/requests", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async createOneOffRequest(data: {
    template: CreateRequestTemplateDto & { pdfDocuments?: BrowserPdfDocument[] };
    request: Omit<CreateRequestUiDto, "template_id">;
  }): Promise<{ template: RequestTemplate; request: Request }> {
    const { pdfDocuments, ...templateData } = data.template;
    const hasPdfFiles = pdfDocuments?.some((doc) => doc.file !== undefined);

    // For signature templates with PDF files, use multipart/form-data
    if (hasPdfFiles && pdfDocuments) {
      const formData = new FormData();

      // Add template and request data as JSON
      formData.append("template", JSON.stringify(templateData));
      formData.append("request", JSON.stringify(data.request));

      // Add PDF files and their metadata
      pdfDocuments
        .filter((doc) => doc.file !== undefined)
        .forEach(({ file, ...metadata }, index) => {
          formData.append("pdfs", file!);
          formData.append(
            "pdf_metadata",
            JSON.stringify({
              title: metadata.title,
              order: index,
              signature_places: metadata.signaturePlaces,
            }),
          );
        });

      const headers: HeadersInit = {};
      if (this.authToken) {
        headers["Authorization"] = `Bearer ${this.authToken}`;
      }

      const response = await fetch(`${this.baseUrl}/requests/one-off`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          throw new Error(error.message || "Failed to create one-off request");
        }
        throw new Error(
          `Failed to create one-off request with status ${response.status}`,
        );
      }

      return response.json();
    }

    // For form templates or when no PDF files, use JSON
    return this.request<{ template: RequestTemplate; request: Request }>(
      "/requests/one-off",
      {
        method: "POST",
        body: JSON.stringify({ template: templateData, request: data.request }),
      },
    );
  }

  async cancelRequest(requestId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/requests/${requestId}/cancel`, {
      method: "PUT",
    });
  }

  async updateRequest(
    requestId: string,
    data: UpdateRequestDto,
  ): Promise<Request> {
    return this.request<Request>(`/requests/${requestId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async resendRequest(
    requestId: string,
    participantIds?: string[],
  ): Promise<void> {
    return this.request<void>(`/requests/${requestId}/resend`, {
      method: "POST",
      body: JSON.stringify({ participantIds }),
    });
  }

  // ---------------------------
  // Participants
  // ---------------------------
  async addParticipantToRequest(
    requestId: string,
    participant: any,
  ): Promise<any> {
    return this.request<any>(`/requests/${requestId}/participants`, {
      method: "POST",
      body: JSON.stringify(participant),
    });
  }

  async removeParticipant(participantId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/participants/${participantId}`, {
      method: "DELETE",
    });
  }

  async updateParticipantReminders(
    participantId: string,
    reminders: any[],
  ): Promise<any> {
    return this.request<any>(`/participants/${participantId}/reminders`, {
      method: "PUT",
      body: JSON.stringify({ reminders }),
    });
  }

  async scheduleParticipantReminders(
    participantId: string,
    dueDate: string,
    reminders: Array<{ timing: string; offset_days: number }>,
  ): Promise<unknown> {
    return this.request<unknown>(`/participants/${participantId}/reminders`, {
      method: "POST",
      body: JSON.stringify({ dueDate, reminders }),
    });
  }

  async resolveParticipantTemplateToken(token: string) {
    return this.request(`/participants/resolve-template/${token}`, {
      method: "GET",
    });
  }

  // ---------------------------
  // Feedback
  // ---------------------------
  async submitFeedback(data: {
    useCase: string;
    painPoint: string;
  }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>("/feedback", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ---------------------------
  // Search
  // ---------------------------
  async search(query: string, organizationId: string): Promise<SearchResult> {
    const params = new URLSearchParams({
      q: query,
      organizationId,
    });
    return this.request<SearchResult>(`/search?${params.toString()}`);
  }
}

export const apiClient = new ApiClient();
export type {
  UploadFileRequest,
  UploadFileResponse,
  FileShareResponse,
  ListSharesResponse,
};
